import type { PropsWithChildren } from "react";
import { useEffect } from "react";

import { useTheme, useColorMode } from "@/shared/store/theme-store";

/**
 * 主题副作用处理：监听主题和模式变化，更新 DOM。
 * 通过 ThemeProvider 组件统一挂载。
 */
function useThemeEffect() {
  const theme = useTheme();
  const colorMode = useColorMode();

  useEffect(() => {
    const root = window.document.documentElement;

    root.setAttribute("data-theme", theme);
    root.classList.remove("light", "dark");

    if (colorMode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const systemMode = mediaQuery.matches ? "dark" : "light";

      root.style.colorScheme = systemMode;
      root.classList.add(systemMode);

      const handleChange = (e: MediaQueryListEvent) => {
        const newMode = e.matches ? "dark" : "light";
        root.style.colorScheme = newMode;
        root.classList.remove("light", "dark");
        root.classList.add(newMode);
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => {
        mediaQuery.removeEventListener("change", handleChange);
      };
    }

    root.style.colorScheme = colorMode;
    root.classList.add(colorMode);
  }, [theme, colorMode]);
}

export function ThemeProvider({ children }: PropsWithChildren) {
  useThemeEffect();

  return children;
}
